import { AlertTriangle } from "lucide-react";

import MullionModelChart from "./MullionModelChart";

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center rounded px-2 py-0.5 font-mono text-[11px] font-semibold ${status === "PASS" ? "status-safe" : "status-danger"}`}>{status}</span>
);

const CheckRow = ({ c }) => {
  const pct = Math.min(c.utilization * 100, 100);
  const color = c.utilization > 1 ? "bg-red-500" : c.utilization > 0.85 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div data-testid={`check-${c.id}`} className="grid grid-cols-12 items-center gap-3 border-t border-zinc-800/70 py-2.5 text-sm first:border-t-0">
      <div className="col-span-12 sm:col-span-5 text-zinc-200">{c.name}</div>
      <div className="col-span-7 sm:col-span-4">
        <div className="h-1.5 overflow-hidden rounded-full bg-zinc-800"><div className={`h-full ${color} transition-[width] duration-500`} style={{ width: `${pct}%` }} /></div>
        <div className="mt-1 font-mono text-[10px] text-zinc-500">{c.demand} / {c.capacity} {c.unit}</div>
      </div>
      <div className="col-span-3 sm:col-span-2 text-right font-mono text-xs tabular-nums text-white">{(c.utilization * 100).toFixed(0)}%</div>
      <div className="col-span-2 sm:col-span-1 text-right"><StatusBadge status={c.status} /></div>
    </div>
  );
};

const KV = ({ k, v }) => (
  <div className="flex justify-between gap-3 border-t border-zinc-800/60 py-1.5 text-sm first:border-t-0">
    <span className="text-zinc-500">{k}</span><span className="text-right font-mono text-zinc-200">{v}</span>
  </div>
);

const Box = ({ title, children, testid }) => (
  <div className="rounded-lg border border-zinc-800 bg-[#121215] p-4" data-testid={testid}>
    <div className="mb-2 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{title}</div>
    {children}
  </div>
);

export default function ResultsPanel({ result }) {
  const { wind, glass, anchor } = result;
  return (
    <div className="space-y-5" data-testid="results-panel">
      {result.warnings?.map((w) => (
        <div key={w} className="flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="design-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{w}
        </div>
      ))}
      {result.mullion_model && <MullionModelChart m={result.mullion_model} />}
      <Box title="Structural checks" testid="checks-box">
        {result.checks.map((c) => <CheckRow key={c.id} c={c} />)}
      </Box>
      <div className="grid gap-4 md:grid-cols-2">
        {result.members.map((m) => (
          <Box key={m.role} title={`${m.role} — selected profile`} testid={`member-${m.role.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="mb-2 font-[Manrope] text-xl font-bold text-sky-300">{m.profile}</div>
            <KV k="D × B × t" v={`${m.depth_mm} × ${m.width_mm} × ${m.t_mm} mm`} />
            <KV k="Ixx / Iyy" v={`${m.ix_cm4} / ${m.iy_cm4} cm⁴`} />
            <KV k="Zxx" v={`${m.zx_cm3} cm³`} />
            <KV k="Weight" v={`${m.weight_kg_m} kg/m`} />
            <KV k="Span" v={`${m.span_mm} mm`} />
            <KV k="Moment / Shear" v={`${m.moment_kNm} kNm / ${m.shear_kN} kN`} />
            <KV k="Deflection" v={`${m.deflection_mm} ≤ ${m.deflection_limit_mm} mm`} />
          </Box>
        ))}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Box title="Wind (design)" testid="wind-box">
          <div className="mb-1 font-[Manrope] text-xl font-bold text-amber-300" data-testid="design-wind-pressure">{wind.design_kpa} kPa</div>
          <KV k="Source" v={wind.source} />
          {wind.vz && <KV k="Vz / k2" v={`${wind.vz} m/s / ${wind.k2}`} />}
          {wind.pd_kpa && <KV k="pd × Cp,net" v={`${wind.pd_kpa} × ${wind.cp_net}`} />}
        </Box>
        <Box title="Glass" testid="glass-box">
          <p className="mb-1 text-sm text-zinc-200">{glass.type}</p>
          <KV k="Panel" v={`${glass.panel_w_mm} × ${glass.panel_h_mm} mm`} />
          <KV k="Stress" v={`${glass.stress_mpa} / ${glass.allowable_mpa} MPa`} />
          <KV k="Deflection" v={`${glass.deflection_mm} / ${glass.deflection_limit_mm} mm`} />
        </Box>
        <Box title="Anchorage" testid="anchor-box">
          <p className="mb-1 text-sm text-zinc-200">{anchor.anchor}</p>
          <KV k="Demand" v={`${anchor.demand_kN} kN`} />
          <KV k="Wind / Dead" v={`${anchor.wind_reaction_kN} / ${anchor.dead_reaction_kN} kN`} />
          <p className="mt-2 text-xs text-zinc-500">{anchor.bracket}</p>
        </Box>
      </div>
    </div>
  );
}
